import dayjs from 'dayjs'
import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Box,
  Typography,
  CircularProgress,
  Breadcrumbs,
  Grid,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material'
import { Clear } from '@mui/icons-material'
import { IconCirclePlus } from '@tabler/icons-react'
import MainCard from '../../../../components/ui/MainCard'
import TableRender from '../../../../components/TableRender'
import BoxWrapper from '../../../../components/BoxWrapper'
import LeadItem from '../../components/LeadItem'
import atsProviders from '../../../../config/atsProviders'
import CreateLeadForOrder from '../../../Leads/CreateLeadForOrder'
import SuccessModal from '../../../../components/SuccessModal'
import BulkLeadActions from '../components/BulkLeadActions'
import classes from './style.module.scss'
import axiosInstance from '../../../../utils/axiosInstance'
import { useDispatch, useSelector } from '../../../../store/store'
import { removeSelected } from '../../../../store/slices/table'
import { leadStatuses } from '../../../../constants'
import { classifyLeadField, firstFieldValue } from '../../../../utils/leadFields'
import { LeadEmailHistoryEntry } from '../../components/LeadEmailHistory'
import { AtsPushState } from '../../../Leads/components/AtsPushStatusNote'

// The atsPush* fields arrive flat from the API; createData groups them into `atsPush`.
export interface Lead extends AtsPushState {
  id: string
  createdAt: string
  status?: string
  platform: string
  fieldData: any
  firstName: string
  lastName: string
  phone: string
  email: string
  question?: {
    name: string
    value: string
  }
  emailHistory?: LeadEmailHistoryEntry[]
  applicantCV?: {
    id: string
    name: string
    file_filename: string
    file_filesize: number
    createdAt: string
  } | null
  atsPush?: AtsPushState
}

interface Order {
  id: string
  jobName: string
  customer: {
    id: string
    name: string
  }
  jobs?: Array<{
    atsAuthConfig?: {
      type: string
    }
  }>
}

interface LeadsByOrderProps {
  orderId?: string // Make orderId optional prop
  hideHeader?: boolean // Optional prop to hide the header section
  hideWrapper?: boolean // Optional prop to use custom wrapper
}

function createData(
  id: string,
  createdAt: string,
  firstName: string,
  lastName: string,
  phone: string,
  email: string,
  platform: string,
  question: {
    name: string
    value: string
  },
  status: string | undefined = undefined,
  fieldData: any = {},
  emailHistory?: LeadEmailHistoryEntry[],
  applicantCV?: {
    id: string
    name: string
    file_filename: string
    file_filesize: number
    createdAt: string
  } | null,
  atsPush?: AtsPushState
): Lead {
  return {
    id,
    createdAt,
    firstName,
    lastName,
    phone,
    email,
    platform,
    question,
    status,
    fieldData,
    emailHistory,
    applicantCV,
    atsPush,
  }
}

// Map a form field to a standard applicant field; misspelled keys ("full name") still match.
const getFieldType = (fieldName: string): string => classifyLeadField(fieldName) || 'question'

// Helper function to normalize platform values
const normalizePlatform = (platformValue: string) => {
  if (!platformValue) return null

  const lower = platformValue.toLowerCase()

  // Handle common variations
  // Audience Network ('an') and Messenger ('msg') are Meta/Facebook surfaces,
  // so we group them under the Facebook platform for display.
  if (lower === 'fb' || lower === 'an' || lower === 'msg' || lower.includes('facebook')) {
    return 'facebook'
  }
  if (lower === 'ig' || lower.includes('instagram')) {
    return 'instagram'
  }
  if (lower.includes('internal') || lower.includes('recruspin')) {
    return 'internalRecruspin'
  }
  if (lower.includes('tiktok')) {
    return 'tiktok'
  }
  if (lower.includes('spotify')) {
    return 'spotify'
  }
  if (lower.includes('youtube')) {
    return 'youtube'
  }
  if (lower.includes('linkedin')) {
    return 'linkedin'
  }
  if (lower.includes('google')) {
    return 'google'
  }
  if (lower === 'x' || lower.includes('twitter')) {
    return 'x'
  }

  // Return the original value if it doesn't match any pattern
  return platformValue
}

const LeadsByOrder = ({
  orderId: propOrderId,
  hideHeader = false,
  hideWrapper = false,
}: LeadsByOrderProps = {}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { orderId: urlOrderId } = useParams<{ orderId: string }>()
  const { selected } = useSelector((state) => state.table)

  // Use prop orderId if provided, otherwise fall back to URL param
  const orderId = propOrderId || urlOrderId

  const [page, setPage] = useState(0)
  const defaultRowsPerPage = Number(localStorage.getItem('leadsByOrderTableRowsPerPage')) || 10
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [leads, setLeads] = useState<Lead[]>([])
  const [rows, setRows] = useState<Lead[]>([])
  const [order, setOrder] = useState<Order | null>(null)

  // State for the modal
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isExportingAll, setIsExportingAll] = useState(false)
  const [isExportingNew, setIsExportingNew] = useState(false)
  const [isCreateLeadModalOpen, setIsCreateLeadModalOpen] = useState(false)
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null)
  const [atsSuccessModalOpen, setAtsSuccessModalOpen] = useState(false)
  const [atsResponseId, setAtsResponseId] = useState<string | null>(null)
  const theme = useTheme()

  // Handle clearing selection
  const handleClearSelection = () => {
    dispatch(removeSelected())
  }

  // Handle status update - refresh the data
  const handleStatusUpdate = () => {
    refetchLeads()
  }

  const statusCounter = (data, status) => ({
    [status]: data?.filter((item) => item.status === status).length,
  })

  const StatsItem = ({ status: { label, value }, rows }) => {
    const getStatuses = statusCounter(rows, value)

    return (
      <Grid
        item
        xs="auto"
        container
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px',
          borderRadius: '8px',
          flex: '1 1 auto',
          border: '1px solid  #EEE',
        }}
      >
        <p className={`${classes.status} ${classes[value]}`}>{label}</p>
        <p
          className={`${classes.status} ${classes[value]}`}
          style={{ width: '20px', height: '20px' }}
        >
          {getStatuses[value]}
        </p>
      </Grid>
    )
  }

  // Handle row click to open the modal
  const handleRowClick = useCallback(
    (leadId: string) => {
      const lead = rows.find((row) => row.id === leadId)
      if (lead) {
        setSelectedLead(lead)
        setIsModalOpen(true)
        navigate({ search: `id=${leadId}` })
      }
    },
    [rows, navigate]
  )

  // Close the modal
  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleAtsSuccess = (responseId?: string) => {
    // Set both states - React 18 batches these together
    setAtsResponseId(responseId || null)
    setAtsSuccessModalOpen(true)
  }

  // Refresh leads data
  const refetchLeads = async () => {
    if (orderId) {
      try {
        setLoading(true)
        const response = await axiosInstance.get(`/leads-by-order/${orderId}`)
        setLeads(response.data.leads || [])
        setOrder(response.data.order || null)
        setError(false)
      } catch (err) {
        console.error('Error fetching leads by order:', err)
        setError(true)
      } finally {
        setLoading(false)
      }
    }
  }

  // Export all leads
  const handleExportAll = async () => {
    if (!orderId) return

    setIsExportingAll(true)
    try {
      const response = await axiosInstance.get(`/leads-by-order/${orderId}/download`, {
        responseType: 'blob',
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const fileName = `order-${orderId}-all-leads.csv`
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      // Update localStorage to mark all current leads as downloaded
      const storageKey = `downloaded-leads-order-${orderId}`
      const allLeadIds = leads.map((lead) => lead.id)
      const downloadedLeadsJson = localStorage.getItem(storageKey)
      const previouslyDownloaded = downloadedLeadsJson ? JSON.parse(downloadedLeadsJson) : []
      const updatedDownloadedLeads = [...new Set([...previouslyDownloaded, ...allLeadIds])]
      localStorage.setItem(storageKey, JSON.stringify(updatedDownloadedLeads))
    } catch (error) {
      console.error('Error exporting all leads:', error)
    } finally {
      setIsExportingAll(false)
    }
  }

  // Export new leads (not downloaded before)
  const handleExportNew = async () => {
    if (!orderId) return

    setIsExportingNew(true)
    try {
      // Get the list of previously downloaded lead IDs from localStorage
      const storageKey = `downloaded-leads-order-${orderId}`
      const downloadedLeadsJson = localStorage.getItem(storageKey)
      const downloadedLeadIds = downloadedLeadsJson ? JSON.parse(downloadedLeadsJson) : []

      // Filter leads to get only new ones
      const newLeads = leads.filter((lead) => !downloadedLeadIds.includes(lead.id))

      if (newLeads.length === 0) {
        // eslint-disable-next-line no-alert
        alert('No new leads to download. All leads have been downloaded before.')
        return
      }

      // Get the IDs of new leads
      const newLeadIds = newLeads.map((lead) => lead.id)
      const leadIdsParam = newLeadIds.join(',')

      const response = await axiosInstance.get(`/leads-by-order/${orderId}/download`, {
        params: { leadIds: leadIdsParam },
        responseType: 'blob',
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const fileName = `order-${orderId}-new-leads.csv`
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      // Update localStorage with the newly downloaded lead IDs
      const updatedDownloadedLeads = [...downloadedLeadIds, ...newLeadIds]
      localStorage.setItem(storageKey, JSON.stringify(updatedDownloadedLeads))
    } catch (error) {
      console.error('Error exporting new leads:', error)
    } finally {
      setIsExportingNew(false)
    }
  }

  // Get count of new leads for tooltip
  const getNewLeadsCount = () => {
    if (!orderId || leads.length === 0) return 0
    const storageKey = `downloaded-leads-order-${orderId}`
    const downloadedLeadsJson = localStorage.getItem(storageKey)
    const downloadedLeadIds = downloadedLeadsJson ? JSON.parse(downloadedLeadsJson) : []
    return leads.filter((lead) => !downloadedLeadIds.includes(lead.id)).length
  }

  // Get count of previously downloaded leads
  const getDownloadedLeadsCount = () => {
    if (!orderId || leads.length === 0) return 0
    const storageKey = `downloaded-leads-order-${orderId}`
    const downloadedLeadsJson = localStorage.getItem(storageKey)
    const downloadedLeadIds = downloadedLeadsJson ? JSON.parse(downloadedLeadsJson) : []
    return leads.filter((lead) => downloadedLeadIds.includes(lead.id)).length
  }

  // Clear download history
  const clearDownloadHistory = () => {
    if (!orderId) return
    const storageKey = `downloaded-leads-order-${orderId}`
    localStorage.removeItem(storageKey)
    // Force re-render to update button states
    setLeads([...leads])
  }

  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(event.currentTarget)
  }

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null)
  }

  const handleExportAllClick = () => {
    handleDownloadMenuClose()
    handleExportAll()
  }

  const handleExportNewClick = () => {
    handleDownloadMenuClose()
    handleExportNew()
  }

  const content = (
    <>
      {!hideHeader && (
        <div className={classes.tableHeader}>
          <div className={classes.header}>
            <div className={classes.titleContainer}>
              <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}></Breadcrumbs>
              <Typography variant="h2">
                {order?.jobName || 'Job'} - {order?.customer?.name || 'Customer'}
              </Typography>
              <span className={classes.counter}>({rows.length || 0})</span>
            </div>
            <Tooltip title="Download CSV">
              <IconButton onClick={handleDownloadMenuOpen}>
                <DownloadIcon style={{ color: '#757575', height: '1em' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Create Lead">
              <IconButton onClick={() => setIsCreateLeadModalOpen(true)}>
                <IconCirclePlus
                  stroke="none"
                  size="35px"
                  fill={theme.palette.primary.main}
                  color={theme.palette.primary.light}
                />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      )}

      {/* Action buttons - always visible even when header is hidden */}
      {hideHeader && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            px: 2,
          }}
        >
          <Tooltip title="Download CSV">
            <IconButton onClick={handleDownloadMenuOpen}>
              <DownloadIcon style={{ color: '#757575', height: '1em' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Create Lead">
            <IconButton onClick={() => setIsCreateLeadModalOpen(true)}>
              <IconCirclePlus
                stroke="none"
                size="35px"
                fill={theme.palette.primary.main}
                color={theme.palette.primary.light}
              />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Download Menu */}
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={handleDownloadMenuClose}
      >
        <MenuItem onClick={handleExportAllClick} disabled={isExportingAll || leads.length === 0}>
          {isExportingAll ? 'Exporting...' : 'Export All'}
        </MenuItem>
        <MenuItem
          onClick={handleExportNewClick}
          disabled={isExportingNew || getNewLeadsCount() === 0}
        >
          {isExportingNew ? 'Exporting...' : `Export New (${getNewLeadsCount()})`}
        </MenuItem>
      </Menu>

      <Grid container justifyContent="space-between" padding={'16px'} gap={2}>
        {loading ? (
          <CircularProgress />
        ) : (
          leadStatuses.map((status) => <StatsItem key={status.value} status={status} rows={rows} />)
        )}
      </Grid>

      {/* Download history info - if applicable */}
      {getDownloadedLeadsCount() > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            px: 2,
            mb: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Downloaded: {getDownloadedLeadsCount()}/{leads.length}
          </Typography>
          <Tooltip title="Clear download history - this will reset tracking and show all leads as 'new' again">
            <IconButton
              size="small"
              onClick={clearDownloadHistory}
              sx={{ color: 'text.secondary' }}
            >
              <Clear fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Bulk Lead Actions - appears when leads are selected */}
      <BulkLeadActions
        selectedLeads={selected}
        onClearSelection={handleClearSelection}
        onStatusUpdate={handleStatusUpdate}
        orderId={orderId}
      />

      <TableRender
        data={rows}
        type={'lead'}
        defaultOrderBy={'createdAt'}
        isLoading={loading}
        toolbar
        pagination
        densePadding
        checkbox={true}
        page={page}
        rowsPerPage={rowsPerPage}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        rowsPerPageItemName="leadsByOrderTableRowsPerPage"
        onRowClick={handleRowClick}
      />

      {/* Lead Item Modal */}
      <LeadItem
        lead={
          selectedLead || {
            id: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            platform: '',
            createdAt: '',
            fieldData: {},
          }
        }
        orderJobs={order?.jobs}
        orderId={orderId}
        setSelectedLead={setSelectedLead}
        atsProviders={atsProviders}
        open={isModalOpen && selectedLead !== null}
        onClose={handleCloseModal}
        refetch={refetchLeads}
        onAtsSuccess={handleAtsSuccess}
      />

      {/* Create Lead Modal */}
      {orderId && (
        <CreateLeadForOrder
          open={isCreateLeadModalOpen}
          setOpen={setIsCreateLeadModalOpen}
          orderId={orderId}
          onSuccess={refetchLeads}
        />
      )}

      {/* ATS Success Modal */}
      <SuccessModal
        successModalOpen={atsSuccessModalOpen}
        handleClose={() => {
          setAtsSuccessModalOpen(false)
          setAtsResponseId(null)
        }}
        setSuccessModalOpen={setAtsSuccessModalOpen}
        message={
          atsResponseId ? (
            <>
              Lead sent to ATS successfully
              <Typography variant="body2" sx={{ mt: 1 }}>
                ATS ID: <strong>{atsResponseId}</strong>
              </Typography>
            </>
          ) : (
            'Lead sent to ATS successfully'
          )
        }
        zIndex={1500}
      />
    </>
  )

  // Fetch data from API
  useEffect(() => {
    const fetchLeadsByOrder = async () => {
      try {
        setLoading(true)
        const response = await axiosInstance.get(`/leads-by-order/${orderId}`)
        setLeads(response.data.leads || [])
        setOrder(response.data.order || null)
        setError(false)
      } catch (err) {
        console.error('Error fetching leads by order:', err)
        setError(true)
      } finally {
        setLoading(false)
      }
    }

    if (orderId) {
      fetchLeadsByOrder()
    }
  }, [orderId])

  // Process leads data to match the format used in LeadsList
  useEffect(() => {
    if (leads.length > 0) {
      const processedLeads = leads.map((lead) => {
        const {
          id,
          createdAt,
          status,
          platform: rawPlatform,
          fieldData,
          emailHistory,
          applicantCV,
          atsPushStatus,
          atsPushDueAt,
          atsPushMessage,
          atsPushAttempts,
        } = lead
        const reformat = {
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          question: { name: '', value: '' },
        }

        try {
          const parsedFields = typeof fieldData === 'string' ? JSON.parse(fieldData) : fieldData

          if (Array.isArray(parsedFields)) {
            parsedFields.forEach((item) => {
              if (!item || !item.name) return

              const fieldType = getFieldType(item.name)
              const value = firstFieldValue(item)

              switch (fieldType) {
                case 'full_name': {
                  // Split name if possible, otherwise use full value as firstName
                  const nameParts = value.split(' ')
                  reformat.firstName = nameParts[0] || value
                  reformat.lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''
                  break
                }
                case 'first_name':
                  if (!reformat.firstName) reformat.firstName = value
                  break
                case 'last_name':
                  if (!reformat.lastName) reformat.lastName = value
                  break
                case 'email':
                  reformat.email = reformat.email || value
                  break
                case 'phone_number':
                  reformat.phone = reformat.phone || value
                  break
                default:
                  reformat.question = {
                    name: item.name,
                    value: Array.isArray(item.values)
                      ? item.values.join(', ')
                      : String(item.values || ''),
                  }
                  break
              }
            })
          } else {
            // Handle non-array fieldData format
            reformat.firstName = parsedFields.firstName || parsedFields.name || ''
            reformat.lastName = parsedFields.lastName || ''
            reformat.email = parsedFields.email || ''
            reformat.phone = parsedFields.phone || parsedFields.phoneNumber || ''
            if (parsedFields.question) {
              reformat.question = {
                name: 'Question',
                value: parsedFields.question,
              }
            }
          }
        } catch (error) {
          console.error('Error parsing field data:', error)
        }

        const { firstName, lastName, phone, email, question } = reformat
        const platform = normalizePlatform(rawPlatform)

        return createData(
          id,
          dayjs(createdAt).format(),
          firstName,
          lastName,
          phone,
          email,
          platform as string,
          question,
          status,
          fieldData,
          emailHistory,
          applicantCV,
          { atsPushStatus, atsPushDueAt, atsPushMessage, atsPushAttempts }
        )
      })

      setRows(processedLeads)
    }
  }, [leads])

  useEffect(() => {
    if (window.location.search) {
      handleRowClick(new URLSearchParams(window.location.search).get('id') || '')
    }
  }, [leads, handleRowClick])

  // If loading, show loading indicator
  if (loading) {
    const loadingContent = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress />
      </Box>
    )

    if (hideWrapper) {
      return loadingContent
    }

    return <MainCard title={intl.formatMessage({ id: 'leads' })}>{loadingContent}</MainCard>
  }

  // If error, show error message
  if (error) {
    const errorContent = <Typography color="error">Error loading leads data</Typography>

    if (hideWrapper) {
      return errorContent
    }

    return <MainCard title={intl.formatMessage({ id: 'leads' })}>{errorContent}</MainCard>
  }

  if (hideWrapper) {
    return content
  }

  return <BoxWrapper height="100%">{content}</BoxWrapper>
}

export default LeadsByOrder
